import { useSelector } from 'react-redux';
import { salesApi, useCreateSubscriptionMutation, useFetchSubscriptionPlansQuery } from '../service';
import { getUserEmail } from '../../store/iam/selectors';
import { useState } from 'react';
import {
    sendFreeTrialStartedEvent,
    sendBingFreeTrialStartedEvent,
    sendOpenAiTrialStartedEvent,
    sendPurchaseEvent,
    sendBingPurchaseEvent
} from '../../utils/analytics';
import { getPlanDetails } from '../utils';
import { useNavigate } from "react-router-dom";
import { createOrganizationIfNotExists, refreshAccessToken } from '../../store/iam/operations';
import { useAppDispatch } from '../../useAppDispatch';
import { CreateSubscriptionParams } from '../types';

const initialState = {
    creditCardToken: '',
    threeDSecureActionToken: '',
};

export default function useCreateAndProcessSubscription() {
    const dispatch = useAppDispatch(); // TODO Remove after IAM Refactor
    const navigate = useNavigate();

    const [pendingPaymentTokens, setPendingPaymentTokens] = useState(initialState);

    const { data: plans } = useFetchSubscriptionPlansQuery();
    const [createSubscription, { isLoading: isCreatingSubscription }] = useCreateSubscriptionMutation();
    const userEmail = useSelector((state) => getUserEmail(state)); // TODO Refactor after IAM refactor

    const removePendingTokens = () => {
        setPendingPaymentTokens(initialState);
    };

    const createAndProcessSubscription = async (token: string, paymentMethod: string, planCode: string, redirectTo?: string, threeDSecureToken?: string) => {
        if (!plans) {
            return { error: 'Something went wrong. Please try again' };
        }

        const payload: CreateSubscriptionParams = {
            token,
            user_email: userEmail,
            payment_method: paymentMethod,
            plan_code: planCode,
        };

        if (threeDSecureToken) {
            payload.three_d_secure_action_result_token_id = threeDSecureToken;
        }

        try {
            const response = await createSubscription(payload).unwrap();

            if ('action_token_id' in response) {
                setPendingPaymentTokens({ creditCardToken: response.token_id, threeDSecureActionToken: response.action_token_id });
            } else {
                setPendingPaymentTokens(initialState);

                if ('error' in response) {
                    return { error: response.error };
                }

                if (redirectTo !== '/subscription/plans') {
                    const planDetails = getPlanDetails(plans, planCode);

                    if (planDetails) {
                        try {
                            sendFreeTrialStartedEvent(planCode);
                            sendBingFreeTrialStartedEvent(planCode);
                            sendOpenAiTrialStartedEvent(planCode, planDetails.price);
                            sendPurchaseEvent(planCode, planDetails.name, planDetails.price / 100);
                            sendBingPurchaseEvent(planDetails.price / 100, planDetails.name);
                        } catch (error) { }
                    }
                }

                // TODO REFACTOR AFTER IAM REFACTOR
                await dispatch(createOrganizationIfNotExists());
                await dispatch(refreshAccessToken());

                if (redirectTo) {
                    navigate(redirectTo);
                }
            }
        } catch (error: any) {
            setPendingPaymentTokens(initialState);

            // 409: the customer already has a subscription, so there is nothing to buy - changing the
            // plan is what they need, and it needs no card. Re-read the subscription so the plans page
            // stops offering a purchase.
            if (error?.status === 409) {
                dispatch(salesApi.util.invalidateTags(['Subscription']));
                return { error: 'You already have a subscription. Change your plan from the subscription plans page.' };
            }

            // The provider did not answer. Nothing was charged and nothing was created, so the same
            // attempt can be made again in a few minutes - unlike a refused card.
            if (error?.status === 502 || error?.status === 503) {
                return { error: 'The payment provider is not reachable right now. Please try again in a few minutes.' };
            }

            return { error: 'Cannot create subscription. Please try again or contact support!' };
        }
    };

    return {
        createAndProcessSubscription,
        removePendingTokens,
        isCreatingSubscription,
        pendingPaymentTokens,
    };
}
